export default () => {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      this.currentPage = 1;
      this.productsPerPage = parseInt(this.getAttribute('productsPerPage'), 10);
      this.collectionHandle = this.getAttribute('collectionHandle');

      this.onLoadMoreClick = this.loadMoreProducts.bind(this);
      this.onCollectionUpdated = this.rebind.bind(this);
      this.shouldFocusOnNewProducts = false;
      this.previousProductCount = 0;
    }

    connectedCallback() {
      this.bindElements();
      if (!this.productGrid || !this.loadMoreBtn || !this.progressBar) return;

      this.action();
      document.addEventListener('collection:updated', this.onCollectionUpdated);
    }

    disconnectedCallback() {
      this.loadMoreBtn?.removeEventListener('click', this.onLoadMoreClick);
      document.removeEventListener('collection:updated', this.onCollectionUpdated);
    }

    bindElements() {
      this.totalProductsElement = document.getElementById('products_count');
      this.loadMoreBtn = document.getElementById('load_more_btn');
      this.progressBar = document.getElementById('bar');
      this.productGrid = document.querySelector('#product-grid');

      this.totalProducts = parseInt(this.totalProductsElement?.value ?? '0', 10) || 0;
      this.totalPageNumber = parseInt(this.loadMoreBtn?.getAttribute('data-total-page') ?? '0', 10)
        || (this.productsPerPage ? Math.ceil(this.totalProducts / this.productsPerPage) : 0);
      this.previousProductCount = this.productGrid.querySelectorAll('.product-grid__item').length;
    }

    action() {
      this.loadMoreBtn.removeEventListener('click', this.onLoadMoreClick);
      this.loadMoreBtn.addEventListener('click', () => {
        this.shouldFocusOnNewProducts = false;
        this.onLoadMoreClick()
      });


      this.updateProgress();
      this.hideButton();
      // eslint-disable-next-line
      this.loadMoreBtn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.shouldFocusOnNewProducts = true; // Keyboard activation
          this.loadMoreProducts();
        }
      });
    }

    rebind() {
      this.bindElements();
      if (this.productGrid && this.loadMoreBtn && this.progressBar) {
        this.currentPage = 1;
        this.action();
      }
    }

    hideButton() {
      if (!this.loadMoreBtn) return;
      if (this.currentPage >= this.totalPageNumber || this.totalPageNumber <= 1) {
        this.loadMoreBtn.classList.add('hidden');
      } else {
        this.loadMoreBtn.classList.remove('hidden');
      }
    }

    loadMoreProducts() {
      this.currentPage += 1;
      this.hideButton();

      // Store current product count before fetching new ones
      this.previousProductCount = this.productGrid.querySelectorAll('.product-grid__item').length;

      this.fetchProducts(this.currentPage);
    }

    fetchProducts(page) {
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      params.delete('section_id');
      params.set('view', 'ajax');
      params.set('page', String(page));
      this.loadMoreBtn.setAttribute('aria-busy', 'true');
      this.loadMoreBtn.disabled = true;

      const url = `${window.location.pathname}?${params.toString()}`;

      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newGrid = doc.querySelector('#product-grid');
          const newProductElements = Array.from(newGrid.children);

          if (newGrid) {
            this.productGrid.append(...newGrid.children);
            this.updateProgress();

            // Focus on first new product if triggered by keyboard
            if (this.shouldFocusOnNewProducts && newProductElements.length > 0) {
              this.focusOnFirstNewProduct(newProductElements[0]);
            }

            document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
          }
        })
        // eslint-disable-next-line
        .catch((err) => console.error('Error:', err))
        .finally(() => {
          // Remove loading state
          this.loadMoreBtn.setAttribute('aria-busy', 'false');
          this.loadMoreBtn.disabled = false;
          this.shouldFocusOnNewProducts = false;
        });
    }

    focusOnFirstNewProduct(firstNewProduct) {
      // Find the first focusable element in the new product
      const focusableElement = this.findFocusableElement(firstNewProduct);

      if (focusableElement) {
        // Add a slight delay to ensure DOM is fully updated
        setTimeout(() => {
          focusableElement.focus();

          // Optional: Add temporary focus ring for better visibility
          this.addTemporaryFocusRing(focusableElement);

          // Scroll into view if needed
          focusableElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);
      } else {
        // If no focusable element found, make the product container focusable
        firstNewProduct.setAttribute('tabindex', '-1');
        setTimeout(() => {
          firstNewProduct.focus();
          firstNewProduct.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);
      }
    }

    findFocusableElement(container) {
      // Look for common focusable elements in product items
      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
        '.product-item__title a',
        '.product-link',
        '.product-form button'
      ];

      for (const selector of focusableSelectors) {
        const element = container.querySelector(selector);
        if (element && this.isElementVisible(element)) {
          return element;
        }
      }

      return null;
    }

    isElementVisible(element) {
      const style = window.getComputedStyle(element);
      return style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        style.opacity !== '0';
    }

    addTemporaryFocusRing(element) {
      // eslint-disable-next-line
      element.style.outline = '2px solid #000';
      // eslint-disable-next-line
      element.style.outlineOffset = '2px';

      // Remove after a short delay
      setTimeout(() => {
        // eslint-disable-next-line
        element.style.outline = '';
        // eslint-disable-next-line
        element.style.outlineOffset = '';
      }, 2000);
    }

    announceError() {
      let liveRegion = document.querySelector('.pagination-live-region');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.className = 'pagination-live-region';
        liveRegion.setAttribute('aria-live', 'assertive');
        liveRegion.style.cssText = 'position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;';
        document.body.appendChild(liveRegion);
      }

      liveRegion.textContent = 'Error loading more products. Please try again.';
    }

    updateProgress() {
      if (!this.productGrid || !this.progressBar || !this.totalProducts) return;
      const visibleProducts = this.productGrid.querySelectorAll('.product-grid__item').length;
      const progressPercentage = Math.min(100, (visibleProducts / this.totalProducts) * 100);
      this.progressBar.setAttribute('aria-valuemax', String(this.totalProducts));
      this.progressBar.setAttribute('aria-valuenow', String(progressPercentage));
      this.progressBar.style.width = `${progressPercentage}%`;
    }
  }

  const init = () => {
    if (!customElements.get("fetch-pagination")) {
      customElements.define("fetch-pagination", Pagination);
    }
  };

  init();
};
